import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';

import {AbstractFieldComponent} from '../../../abstract/abstract-field.component';
import {DatepickerStartView} from '../../../../enums/datepicker-start-view.enum';
import {isNullOrUndefined} from 'util';
import {isCarOne, isMobile, noop} from '../../../../../core/utils/utils';
import {MAT_DATE_FORMATS, MatDatepicker, MatDatepickerInputEvent} from '@angular/material';
import {TranslateService} from '@ngx-translate/core';
import {Subscription} from 'rxjs/Rx';
import {MaterialDateFieldConfig} from '../../../../models/material-date-field-config';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {FormFieldService} from '../../../../services/form-field.service';
import {MaterialDatepickerType} from '../../../../enums/material-datepicker-type.enum';

export const FULL_DATE_MODE_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MM YYYY',
    dateA11yLabel: 'DD.MM.YYYY',
    monthYearA11yLabel: 'MM YYYY',
  },
};

@Component({
  selector: 'app-material-datepicker',
  templateUrl: './material-datepicker.component.html',
  styleUrls: [],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: FULL_DATE_MODE_FORMATS }
  ]
})
export class MaterialDatepickerComponent extends AbstractFieldComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('picker') picker: MatDatepicker<MaterialDatepickerComponent>;
  @ViewChild('input') input: ElementRef;

  @Output() opened: EventEmitter<void> = new EventEmitter<void>();

  minDate: Date = null;
  maxDate: Date = null;
  startView: DatepickerStartView = DatepickerStartView.MULTI_YEAR;
  readonly: boolean = true;
  startAt: Date;
  resetSubscription: Subscription = null;
  datepickerToggleIcon: string = 'date_range';
  datepickerToggleImage: string;
  hideCalendarPeriodButton: boolean = false;
  initMinDateWithStartAt: boolean = true;
  loading: boolean = true;
  openPending: boolean = false;
  focused: boolean = false;
  typeToSearchText: string = null;
  inputValue: string = null;

  get isMobile() {
    return isMobile();
  }

  get materialDatefieldConfig(): MaterialDateFieldConfig {
    return this.fieldConfig.materialDateFieldConfig;
  }

  // commented line is definition of original requirements for displaying verification hint
  get verificationHintVisible(): boolean {
    return false;
    // return this.isPredefinedValue && !this.errorsMap.get(this.fieldConfig.fieldKey);
  }

  get isAsync(): boolean {
    return !!this.materialDatefieldConfig.async;
  }

  get loadingDropdownVisible(): boolean {
    return this.isAsync && this.openPending && this.loading;
  }

  get manualInputAllowed(): boolean {
    if (this.isMobile) {
      return false;
    }
    return this.materialDatefieldConfig && isNotNullOrUndefined(this.materialDatefieldConfig.manualInputAllowed) ? this.materialDatefieldConfig.manualInputAllowed : false;
  }

  get hasInputValue(): boolean {
    return isNotNullOrUndefined(this.inputValue) && !!this.inputValue.length;
  }

  get typeToSearchTextVisible(): boolean {
    return this.focused && !!this.typeToSearchText && !this.hasInputValue;
  }

  get skipAnalyticsFieldValue(): boolean {
    if (this.fieldConfig.analyticsConfig && isNotNullOrUndefined(this.fieldConfig.analyticsConfig.skipFieldValue)) {
      return this.fieldConfig.analyticsConfig.skipFieldValue;
    }
    return false;
  }

  get isYearPicker(): boolean {
    return this.materialDatefieldConfig && this.materialDatefieldConfig.type === MaterialDatepickerType.YEAR;
  }

  constructor(public formFieldService: FormFieldService,
              public cd: ChangeDetectorRef,
              public ts: TranslateService) {
    super(formFieldService);
  }

  ngOnInit() {
    this.initFieldAppearance();
    this.handleResetAction();
    this.setStartYear();
    this.handleLoadingEvent();
    if (this.fieldConfig.materialDateFieldConfig) {
      this.initInitMinDateWithStartAt();
      this.initDatepickerToggle();
      this.initHideCalendarPeriodButton();
      this.initDateRange();
      this.initStartView();
      this.initReadonly();
      this.initDefaultDate();
      this.initTypeToSearchText();
    }
    this.handleStatusChanges();
  }

  public ngOnDestroy() {
    if (this.resetSubscription) {
      this.resetSubscription.unsubscribe();
    }
  }

  async valueLabel() {
    if (isNotNullOrUndefined(this.control.value)) {
      return this.input.nativeElement.value;
    }
    return null;
  }

  onOpen() {
    this.openPending = false;
    this.opened.emit();
    this.setStartYear();
    super.onOpen();
    if (this.isMobile) {
      this.createTitleAndCloseButton();
    }
    if (this.hideCalendarPeriodButton) {
      this.hidePeriodButton();
    }
    setTimeout(() => {
      this.input.nativeElement.focus();
      this.cd.detectChanges();
    }, 0);
  }

  onFocusIn() {
    this.focused = true;
  }

  onFocusOut() {
    this.focused = false;
  }

  onInputKeyup(event) {
    if (this.manualInputAllowed) {
      const {value} = event.target;
      this.inputValue = value;
      this.handleDateFromManualInput(value);
    }
  }

  open(programmatically = true): void {
    if (!this.isAsync || !this.loading) {
      this.picker.open();
    } else {
      this.openPending = true;
    }
    if (!programmatically) {
      super.onClick();
    }
  }

  hidePeriodButton(): void {
    setTimeout(() => {
      const calendarPeriodButton = document.querySelector('.mat-datepicker-content .mat-calendar-period-button');
      if (calendarPeriodButton) {
        (<any>calendarPeriodButton).style.display = 'none';
      }
    }, 0);
  }

  onClose(valueSelected = true) {
    super.onClose();
  }

  changeDate(event: MatDatepickerInputEvent<Date>) {
    if (!this.skipAnalyticsFieldValue) {
      this.sendFieldClickEvent();
    }
    this.onChange(event);
  }

  initDateRange(): void {
    this.initMinDate();
    this.initMaxDate();
  }

  onYearSelected(event: any) {
  }

  updateStartAt(year: number) {
    this.startAt = new Date(year + '');
  }

  reset(): void {
    this.control.reset();
  }

  setStartYear(): void {
    if (this.fieldConfig.materialDateFieldConfig.hasOwnProperty('startAt')) {
      let startAt;
      if (typeof this.fieldConfig.materialDateFieldConfig.startAt === 'function') {
        startAt = this.fieldConfig.materialDateFieldConfig.startAt();
      } else {
        startAt = this.fieldConfig.materialDateFieldConfig.startAt;
      }
      if (startAt) {
        this.startAt = new Date(startAt + '');
        this.initMinDateWithStartAt ? this.initMinDate(this.startAt) : noop();
      } else {
        this.startAt = new Date();
        this.initMinDate(new Date('1900-01-01'));
      }
    }
  }

  protected clearInput(): void {
    this.inputValue = null;
    this.control.setValue(null);
  }

  protected handleDateFromManualInput(value: string): void {
  }

  private async sendFieldClickEvent() {
    const value = await this.valueLabel();
    this.formFieldService.onClick(this.fieldConfig, value);
  }

  private initDatepickerToggle(): void {
    if (this.materialDatefieldConfig.datepickerToggleIcon) {
      this.datepickerToggleIcon = this.materialDatefieldConfig.datepickerToggleIcon;
    }
    if (isCarOne()) {
      this.datepickerToggleImage = './assets/images/icons/car-one-datepicker.svg';
    } else if (this.materialDatefieldConfig.datepickerToggleImage) {
      this.datepickerToggleImage = this.materialDatefieldConfig.datepickerToggleImage;
    }
  }

  private initInitMinDateWithStartAt(): void {
      if (isNotNullOrUndefined(this.materialDatefieldConfig.initMinDateWithStartAt)) {
          this.initMinDateWithStartAt = this.materialDatefieldConfig.initMinDateWithStartAt;
      }
  }

  private initHideCalendarPeriodButton(): void {
    if (isNotNullOrUndefined(this.materialDatefieldConfig.hideCalendarPeriodButton)) {
      this.hideCalendarPeriodButton = this.materialDatefieldConfig.hideCalendarPeriodButton;
    }
  }

  private handleResetAction() {
    if (this.fieldConfig.materialDateFieldConfig.hasOwnProperty('reset')) {
      this.resetSubscription = this.fieldConfig.materialDateFieldConfig
        .reset()
        .subscribe(() => {
          this.control.reset();
        })
    }
  }

  private initMinDate(forcedValue?: Date): void {
    const { minDate } = this.fieldConfig.materialDateFieldConfig;

    if (minDate || forcedValue) {
      this.minDate = forcedValue || new Date(minDate);
    }
  }

  private initMaxDate(): void {
    const { maxDate } = this.fieldConfig.materialDateFieldConfig;

    if (maxDate) {
      this.maxDate = new Date(maxDate);
    }
  }

  private initStartView(): void {
    const { startView } = this.fieldConfig.materialDateFieldConfig;

    if (startView) {
      this.startView = startView;
    }
  }

  private initReadonly(): void {
    const { readonly } = this.fieldConfig.materialDateFieldConfig;

    if (isNotNullOrUndefined(readonly)) {
      this.readonly = readonly;
    }
  }

  private initDefaultDate(): void {
    if (this.fieldConfig.materialDateFieldConfig.hasOwnProperty('defaultDate')) {
      const defaultDate = this.fieldConfig.materialDateFieldConfig.defaultDate;
      if (isNullOrUndefined(this.control.value)) {
        this.control.setValue(new Date(defaultDate));
      }
    }
  }

  private initTypeToSearchText() {
    if (isNotNullOrUndefined(this.materialDatefieldConfig.typeToSearchText)) {
      this.typeToSearchText = this.materialDatefieldConfig.typeToSearchText;
    }
  }

  private handleLoadingEvent(): void {
    if (this.isAsync && isNotNullOrUndefined(this.materialDatefieldConfig.loadingEvent)) {
      this.materialDatefieldConfig.loadingEvent.subscribe(loading => {
        this.loading = loading;
        if (!this.loading && this.openPending) {
          this.picker.open();
        }
      });
    }
  }

  private createTitleAndCloseButton() {
    const container = document.querySelector('mat-dialog-container');
    const button = this.createCloseButton();
    const title = this.createTitle();

    container.prepend(title);
    container.appendChild(button);
  }

  private createTitle() {
    const title = document.createElement('h3');
    title.classList.add('mobile-datepicker__title');
    title.innerText = this.ts.instant(this.fieldConfig.caption);

    return title;
  }

  private createCloseButton(): HTMLDivElement {
    const button = document.createElement('div');
    button.classList.add('mobile-datepicker__close-btn');
    button.innerText = this.ts.instant('SELECT_DIALOG_COMPONENT.close');
    button.addEventListener('click', () => {
      this.onClose(false);
      this.picker.close();
    });

    return <HTMLDivElement>button;
  }
}
